import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';

// Layout Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import HomePage from './pages/HomePage';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import ForumPage from './pages/ForumPage';
import ThreadDetailPage from './pages/ThreadDetailPage';
import MarketplacePage from './pages/MarketplacePage';
import ListingDetailPage from './pages/ListingDetailPage';
import MyListingsPage from './pages/MyListingsPage';
import AdminNewsPage from './pages/AdminNewsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import DriverOfTheWeekPage from './pages/DriverOfTheWeekPage';
import AdminDriverOfTheWeekPage from './pages/AdminDriverOfTheWeekPage';
import AdminRacingIntelPage from './pages/AdminRacingIntelPage';
import AdminInterviewsPage from './pages/AdminInterviewsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import ProfileEditPage from './pages/ProfileEditPage';
import InterviewPage from './pages/InterviewPage';
import TracksMapPage from './pages/TracksMapPage';
import SponsorsPage from './pages/SponsorsPage';
import AdminSponsorsPage from './pages/AdminSponsorsPage';

import './App.css';
import { useAnalytics } from './hooks/useAnalytics';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-400 text-xs uppercase tracking-widest">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AuthLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {children}
    </div>
  );
}

function AppRoutes() {
  useAnalytics();
  return (
    <Routes>
      {/* Public Routes with Layout */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/news" element={<Layout><NewsPage /></Layout>} />
      <Route path="/news/:id" element={<Layout><NewsDetailPage /></Layout>} />
      <Route path="/forum" element={<Layout><ForumPage /></Layout>} />
      <Route path="/forum/thread/:id" element={<Layout><ThreadDetailPage /></Layout>} />
      <Route path="/marketplace" element={<Layout><MarketplacePage /></Layout>} />
      <Route path="/marketplace/:id" element={<Layout><ListingDetailPage /></Layout>} />
      <Route path="/driver-of-the-week" element={<Layout><DriverOfTheWeekPage /></Layout>} />
      <Route path="/driver-of-the-week/:id" element={<Layout><DriverOfTheWeekPage /></Layout>} />
      <Route path="/profile/:userId" element={<Layout><ProfilePage /></Layout>} />
      <Route path="/interviews/:slug" element={<Layout><InterviewPage /></Layout>} />
      <Route path="/tracks" element={<Layout><TracksMapPage /></Layout>} />
      <Route path="/sponsors" element={<Layout><SponsorsPage /></Layout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
      <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
      
      {/* Protected Routes */}
      <Route path="/profile/edit" element={
        <ProtectedRoute>
          <Layout><ProfileEditPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/my-listings" element={
        <ProtectedRoute>
          <Layout><MyListingsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/news" element={
        <ProtectedRoute>
          <Layout><AdminNewsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <Layout><AdminUsersPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <Layout><AdminDashboardPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/driver-of-the-week" element={
        <ProtectedRoute>
          <Layout><AdminDriverOfTheWeekPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/racing-intel" element={
        <ProtectedRoute>
          <Layout><AdminRacingIntelPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/interviews" element={
        <ProtectedRoute>
          <Layout><AdminInterviewsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/sponsors" element={
        <ProtectedRoute>
          <Layout><AdminSponsorsPage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
